import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../utils/api';

export default function EventDetail() {
  const { slug } = useParams();

  const { data, isLoading, error } = useQuery({
    queryKey: ['event', slug],
    queryFn: () => api.getEvent(slug),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data?.event) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Event δεν βρέθηκε</h1>
        <Link to="/" className="text-primary-600 hover:text-primary-700">
          Επιστροφή στον χάρτη
        </Link>
      </div>
    );
  }

  const { event } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link to="/" className="text-primary-600 hover:text-primary-700 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Επιστροφή στον χάρτη
          </Link>
        </div>
      </header>

      {/* Event Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Event Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-start">
              {event.logo_url && (
                <img
                  src={event.logo_url}
                  alt={event.name}
                  className="w-24 h-24 object-contain mr-6"
                />
              )}
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{event.name}</h1>
                <div className="flex items-center text-gray-600">
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {new Date(event.start_date).toLocaleDateString('el-GR')} - {new Date(event.end_date).toLocaleDateString('el-GR')}
                </div>
              </div>
            </div>
          </div>

          {/* Event Description */}
          {event.description && (
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Περιγραφή</h2>
              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: event.description }} />
            </div>
          )}

          {/* Event Media */}
          {event.media && event.media.length > 0 && (
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Φωτογραφίες</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {event.media.map((media) => (
                  <img
                    key={media.id}
                    src={media.media_url}
                    alt={media.caption || event.name}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Event POIs */}
          {event.pois && event.pois.length > 0 && (
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Σημεία Ενδιαφέροντος</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {event.pois.map((poi) => (
                  <div key={poi.id} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900">{poi.name}</h3>
                    {poi.role && (
                      <span className="inline-block mt-1 px-2 py-1 text-xs bg-primary-100 text-primary-700 rounded">
                        {poi.role}
                      </span>
                    )}
                    {poi.description && (
                      <p className="mt-2 text-sm text-gray-600">{poi.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
